import { BehaviorSubject } from "rxjs";
import { ApiResponse } from "src/AngularJS/Models/api-response.model";
import { ClearViewUtils } from "src/shared/utils";
import { CvConfigurationServiceBase } from "./cv-configuration-service-base";

export class CvApiServiceBase {
    apiTokenSubject: BehaviorSubject<string>;
    _apiToken: string;
    get apiToken() { return this._apiToken; }
    set apiToken(value: string) {
        this._apiToken = value;
        this.apiTokenSubject.next(value);
    }

    constructor(
        fromSharedService: boolean = false,
        private config?: CvConfigurationServiceBase,
    ) {
        if (fromSharedService)
            return CvApiServiceBase.getServiceBase();

        if (!config)
            this.config = config = CvConfigurationServiceBase.getServiceBase();

        this._apiToken = window['CVAccessToken'];
        this.apiTokenSubject = new BehaviorSubject<string>(this._apiToken);

        const apiTokenScript = document.querySelector('script.accessToken');
        apiTokenScript?.parentNode.removeChild(apiTokenScript);
    }

    static getServiceBase(): CvApiServiceBase {
        return ClearViewUtils.getSharedService(CvApiServiceBase, 'CvApiServiceBase');
    }

    fetch<T>(url: string, method: string, headers?: { [key: string]: string }, body?: BodyInit, abort?: PromiseLike<void>) {
        if (typeof fetch === 'function') {
            const settings: RequestInit = { method };
            if (headers)
                settings.headers = headers;
            if (body)
                settings.body = body;

            if (abort) {
                const controller = new AbortController();
                settings.signal = controller.signal;
                abort.then(_ => controller.abort());
            }

            return fetch(url, settings).then(response => response.json() as Promise<T>);
        } else {
            const settings: JQueryAjaxSettings = { url, method };
            if (headers)
                settings.headers = headers;
            if (body)
                settings.data = body;

            const xhr = $.ajax(settings);

            if (abort) {
                abort.then(_ => xhr.abort());
            }

            return new Promise<T>((resolve, reject) => xhr.done(resolve).fail(reject));
        }
    }

    getAccessHeaders(): { [key: string]: string } {
        const headers = {};
        if (this.apiToken)
            headers['X-ACCESS-TOKEN'] = this.apiToken;
        return headers;
    }

    public async getModuleRenderEndPoint(moduleKey: string): Promise<string> {
        const url = `${this.config.baseUri}/api/v1/modules/renderEndpoint/${moduleKey}`;
        const response = await this.fetch<ApiResponse<string>>(url, "GET", this.getAccessHeaders());

        if (response.status_code === 200 && response.data !== null) {
            if (response.data === '') {
                return this.config.baseUri;
            } else {
                return response.data;
            }
        } else {
            if (response.status_code === 401) {
                const redirectPath = window.location.pathname + window.location.search + window.location.hash;
                window.location.replace(window.location.origin + "/Login.aspx?Redirect=" + encodeURIComponent(redirectPath));
            } else {
                throw response;
            }
        }
    }

    public get<T = unknown>(relativeUrl: string, baseUrl?: string, abort?: PromiseLike<void>): Promise<ApiResponse<T>> {
        if (!baseUrl) {
            baseUrl = this.config.baseUri;
        }

        return this.fetch<ApiResponse<T>>(baseUrl + relativeUrl, "GET", this.getAccessHeaders(), null, abort);
    }

    public post<T = unknown>(relativeUrl: string, data?: any, baseUrl?: string): Promise<ApiResponse<T>> {
        if (!baseUrl) {
            baseUrl = this.config.baseUri;
        }

        let body = data;
        if (typeof data !== 'string') {
            body = JSON.stringify(data);
        }

        const headers = this.getAccessHeaders();
        headers["Content-Type"] = "application/json";

        return this.fetch(baseUrl + relativeUrl, "POST", headers, body);
    }

    public async put<T = unknown>(relativeUrl: string, data?: any, baseUrl?: string): Promise<ApiResponse<T>> {
        if (!baseUrl) {
            baseUrl = this.config.baseUri;
        }

        let body = data;
        if (typeof data !== 'string') {
            body = JSON.stringify(data);
        }

        const headers = this.getAccessHeaders();
        headers["Content-Type"] = "application/json";

        return this.fetch(baseUrl + relativeUrl, "PUT", headers, body);
    }

    public async delete<T = unknown>(relativeUrl: string, data?: any, baseUrl?: string): Promise<ApiResponse<T>> {
        if (!baseUrl) {
            baseUrl = this.config.baseUri;
        }

        let body = data;
        if (typeof data !== 'string') {
            body = JSON.stringify(data);
        }

        return this.fetch(baseUrl + relativeUrl, "DELETE", this.getAccessHeaders(), body);
    }
}
